import { useState, useEffect, useCallback } from 'react';

import { assignInlineVars } from '@vanilla-extract/dynamic';
import cn from 'classnames';

import { colors } from '../../../colors/';
import { typography } from '../../../typography';

import { BasePopover } from './BasePopover';
import * as css from './ToggleSelectButton.css';
import { useToggleSelect } from './useToggleSelect';

import type { BaseSelectButtonProps } from './types';

type ToggleSelectButtonProps = BaseSelectButtonProps & {
    /** Called when the selected option changes; returns undefined when selection is cleared. */
    onChange?: (value: string | undefined) => void;

    /** Currently selected option value (controlled mode). */
    selectedOption?: string;
    direction?: 'up' | 'down';
};

const EMPTY_SELECTION = 'None';
/**
 * @TODO: None is risky here, we are in the middle of localizing our project, what happens then?
 */
export function ToggleSelectButton({
    prefix,
    options,
    variant = 'lighter',
    isDisabled,
    maxLetters,
    selectedOption,
    hasNone = true,
    direction = 'down',
    onChange,
}: ToggleSelectButtonProps) {
    const { isOpen, setIsOpen, ref, handleClick, CaretCircle, dropdownOptions, showEllipsis } = useToggleSelect({
        maxLetters,
        hasNone,
        emptySelection: EMPTY_SELECTION,
        options,
    });
    const [activeSelection, setActiveSelection] = useState<string | undefined>(selectedOption);

    // Sync internal state with external selectedOption prop
    useEffect(() => {
        setActiveSelection(selectedOption);
    }, [selectedOption]);

    const handleSelectionChange = useCallback(
        (option: string) => {
            const newSelection = option === activeSelection || option === EMPTY_SELECTION ? undefined : option;
            setActiveSelection(newSelection);
            if (onChange) {
                onChange(newSelection);
            }
            setIsOpen(false);
        },
        [activeSelection, onChange, setIsOpen],
    );

    const checkIsActive = useCallback((value: string) => value === selectedOption, [selectedOption]);

    return (
        <div className={css.root}>
            <button
                disabled={isDisabled}
                className={css.toggleSelectButton({
                    status: isDisabled ? 'disabled' : isOpen ? 'open' : activeSelection ? 'selected' : 'closed',
                    variant,
                })}
                style={assignInlineVars({ [css.maxLetters]: `${maxLetters ?? 0}` })}
                onClick={handleClick}
            >
                <div className={css.titleGroup}>
                    <span className={cn([typography({ variant: 'h4' }), css.title({ ellipsis: showEllipsis })])}>
                        {prefix}
                    </span>
                    {activeSelection && (
                        <span className={cn([typography({ variant: 'p' }), css.title({ ellipsis: showEllipsis })])}>
                            {activeSelection}
                        </span>
                    )}
                </div>
                <div className={css.caret}>
                    <CaretCircle size={20} fill={colors.blue[500]} weight="fill" />
                </div>
            </button>
            {isOpen && (
                <BasePopover
                    className={css.popover[direction]}
                    heading={
                        direction !== 'up' && (
                            <div className={css.popoverTitle}>
                                <span
                                    className={cn([
                                        typography({ variant: 'h4' }),
                                        css.title({ ellipsis: showEllipsis }),
                                    ])}
                                >
                                    {prefix}
                                </span>
                                <CaretCircle size={20} fill={colors.blue[500]} weight="fill" />
                            </div>
                        )
                    }
                    dropdownOptions={dropdownOptions}
                    handleSelectionChange={handleSelectionChange}
                    ref={ref}
                    checkIsActive={checkIsActive}
                />
            )}
        </div>
    );
}
